import { VehicleNoteFI } from "@/components/ReservationOptions/VehicleNote/enums/VehicleNoteFI.ts";
import { isMandarinoCamp } from "@/helpers/customFeatures.ts";
import { ParcelObjectType } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { Caravan, Tent, Truck } from "lucide-react";
import { ChangeEvent, FocusEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

const vehicleOptions = [
  { value: ParcelObjectType.Camper, labelKey: "campSpecific.camper", Icon: Truck },
  { value: ParcelObjectType.Tent, labelKey: "campSpecific.tent", Icon: Tent },
  { value: ParcelObjectType.Caravan, labelKey: "campSpecific.caravan", Icon: Caravan },
] as const;

/**
 * Camp specific component --> feature determined through data-attribute
 * data-vehicle_note="true"
 */
export const VehicleNote = ({ cartItem }: { cartItem: CartAccommodationItem }) => {
  const { t } = useTranslation();
  const [vehicleSizeError, setVehicleSizeError] = useState<string | null>(null);

  const {
    rootAttributes,
    setCartItemParcelObjectDimensions,
    setCartItemParcelObjectAdditionalTentDimensions,
    setCartItemParcelObjectType,
    setCartItemParcelObjectHasMover,
  } = useEngineStore(
    useShallow((state) => ({
      rootAttributes: state.rootAttributes,
      setCartItemParcelObjectDimensions: state.setCartItemParcelObjectDimensions,
      setCartItemParcelObjectAdditionalTentDimensions: state.setCartItemParcelObjectAdditionalTentDimensions,
      setCartItemParcelObjectType: state.setCartItemParcelObjectType,
      setCartItemParcelObjectHasMover: state.setCartItemParcelObjectHasMover,
    })),
  );

  const areDimensionsMandatory = rootAttributes[RootAttribute.ParcelVehiclePickerDimensionsMandatory];
  const isParcel = cartItem?.rateGroup?.isParcel;

  if (rootAttributes[RootAttribute.HideParcelVehiclePicker] || !isParcel) return null;

  const selectedType = cartItem?.parcelObjectType || ParcelObjectType.Camper;

  const handleTypeChange = (type: ParcelObjectType) => {
    setCartItemParcelObjectType(type, cartItem?.id);
    if (type !== ParcelObjectType.Caravan) {
      setCartItemParcelObjectHasMover(undefined, cartItem?.id);
    }
  };

  const isCaravan = cartItem.parcelObjectType === ParcelObjectType.Caravan;
  const showMoverQuestion = isCaravan && isMandarinoCamp();

  const moverOptions = [
    { value: "yes", label: t("campSpecific.caravanMoverYes") },
    { value: "no", label: t("campSpecific.caravanMoverNo") },
  ];

  const moverValue =
    cartItem.parcelObjectHasMover === true
      ? "yes"
      : cartItem.parcelObjectHasMover === false
      ? "no"
      : undefined;

  const handleMoverChange = (value: string) => {
    setCartItemParcelObjectHasMover(value === "yes", cartItem.id);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, type: VehicleNoteFI) => {
    const value = e.target.value;
    if (type === VehicleNoteFI.VehicleSize) {
      setCartItemParcelObjectDimensions(value, cartItem?.id);
    } else if (type === VehicleNoteFI.AdditionalTentSize) {
      setCartItemParcelObjectAdditionalTentDimensions(value, cartItem?.id);
    }

    if (value && type === VehicleNoteFI.VehicleSize) {
      setVehicleSizeError(null);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>, type: VehicleNoteFI) => {
    if (areDimensionsMandatory && type === VehicleNoteFI.VehicleSize && !e.target.value) {
      setVehicleSizeError(t("form.requiredField"));
    }
  };

  return (
    <div className="c-vehicle-note">
      <p className="c-vehicle-note__title">{t("campSpecific.parcelVehicle")}</p>

      <div className="c-vehicle-note__content">
        <div className="c-vehicle-note__types">
          {vehicleOptions.map(({ value, labelKey, Icon }) => {
            const active = selectedType === value;
            return (
              <button
                key={value}
                type="button"
                className={`c-vehicle-note__tag ${active ? "c-vehicle-note__tag--active" : ""}`}
                onClick={() => handleTypeChange(value)}
              >
                <span
                  className={`c-vehicle-note__tag-icon ${active ? "c-vehicle-note__tag-icon--active" : ""}`}
                >
                  <Icon size={20} />
                </span>
                <span className="c-vehicle-note__tag-label">{t(labelKey)}</span>
              </button>
            );
          })}
        </div>

        <div className="c-vehicle-note__inputs">
          <div className="c-vehicle-note__field">
            <label className="c-vehicle-note__label" htmlFor={VehicleNoteFI.VehicleSize}>
              {t("campSpecific.size")}
              {areDimensionsMandatory && " *"}
            </label>
            <input
              className={`c-vehicle-note__input ${vehicleSizeError ? "c-vehicle-note__input--error" : ""}`}
              id={VehicleNoteFI.VehicleSize}
              name={VehicleNoteFI.VehicleSize}
              type="text"
              placeholder={t("campSpecific.vehicleNotePlaceholder")}
              value={cartItem.parcelObjectDimensions ?? ""}
              onChange={(e) => handleInputChange(e, VehicleNoteFI.VehicleSize)}
              required={areDimensionsMandatory}
              onBlur={(e) => handleBlur(e, VehicleNoteFI.VehicleSize)}
            />
            {vehicleSizeError && <span className="c-vehicle-note__error">{vehicleSizeError}</span>}
          </div>
          <div className="c-vehicle-note__field">
            <label className="c-vehicle-note__label" htmlFor={VehicleNoteFI.AdditionalTentSize}>
              {t("campSpecific.additionalTentSize")}
            </label>
            <input
              className="c-vehicle-note__input"
              id={VehicleNoteFI.AdditionalTentSize}
              name={VehicleNoteFI.AdditionalTentSize}
              type="text"
              placeholder={t("campSpecific.vehicleNotePlaceholder")}
              value={cartItem.parcelObjectAdditionalTentDimensions ?? ""}
              onChange={(e) => handleInputChange(e, VehicleNoteFI.AdditionalTentSize)}
              onBlur={(e) => handleBlur(e, VehicleNoteFI.AdditionalTentSize)}
            />
          </div>
        </div>

        {showMoverQuestion && (
          <div
            className={`c-vehicle-note__mover ${
              moverValue === undefined ? "c-vehicle-note__mover--error" : ""
            }`}
          >
            <p className="c-vehicle-note__label">{t("campSpecific.caravanHasMover")} *</p>
            <div className="c-vehicle-note__mover-options">
              {moverOptions.map((option) => {
                const active = moverValue === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`c-vehicle-note__pill ${active ? "c-vehicle-note__pill--active" : ""}`}
                    onClick={() => handleMoverChange(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
